/* ===============================================================
   CONFIGURATOR ASSET DRAWER
   assets/configurator-asset-drawer.js

   Shared per-object inspection drawer used by configurator-rack-lib
   (and any future configurator: pool, fence, deck).

   Reuses Shopify Dawn's <side-panel> web component: open = add
   `.active` to drawer + `.open-cc` to <body>; close = remove both.
   <side-panel-close> custom element wires its own close listener
   from Dawn, so we don't need to bind a click handler — we just
   listen for the class change via MutationObserver to fire onClose.

   Visual: matches snippets/ux.liquid (LEVA design system) —
   Montserrat font, 16px card radius, sand-gold accent, dark-brown
   buttons, 4px left-border section titles.

   Mounting: DOM is created once on init(). The host configurator
   (rack-lib, pool-lib …) passes:
     • registry — function(id) → instance object (see shape below)
     • onClose  — function(prevId) — fired after the drawer closes
     • labels   — i18n strings (optional; English defaults)
     • drawerScopeClass — extra class added to the drawer (e.g.
                          'rack-asset-drawer') for product-specific
                          tweaks without leaking globally

   Instance shape expected from registry():
     {
       id, name, type,
       boqCode, category, unit,
       projectQuantity, projectUnitPrice, projectTotalPrice,
       position: { x, y, z },
       length,                        // optional per-instance dim
       boqRow,                        // optional raw BoQ row (array)
                                      // — when present and the global
                                      // window.decoratedTitle exists,
                                      // the displayed name is resolved
                                      // from it (localized BoQ name),
                                      // overriding `name`. `name` stays
                                      // the fallback.
       product: {                     // optional Shopify product
         title, handle, url, image, price, currency, unit,
         availableForSale              // optional
       }
     }

   Public API on window.ConfiguratorAssetDrawer:
     init(opts)        — mount DOM, wire listeners
     open(instanceId)  — open with content (or auto-switch if open)
     close()           — close drawer (also fires onClose)
     isOpen()          — boolean
     getSelected()     — current instance id or null
   =============================================================== */

(function (global) {
  'use strict';

  var VERSION = 'asset-drawer-v19';
  var DRAWER_ID = 'Configurator-Asset-Drawer';

  /* ---------------------------------------------------------------
     STATE
     --------------------------------------------------------------- */
  var _registry = null;
  var _onClose  = null;
  var _currentInstanceId = null;
  var _drawerScopeClass  = '';
  var _suppressNextObserverClose = false;

  /* ---------------------------------------------------------------
     I18N — overridable from init({ labels: {...} })
     --------------------------------------------------------------- */
  var _labels = {
    titlePlaceholder:    'Asset',
    boqSection:          'BoQ',
    boqName:             'Name',
    boqCode:             'Code',
    boqCategory:         'Category',
    boqUnit:             'Unit',
    boqQuantity:         'Project quantity',
    boqUnitPrice:        'Unit price',
    boqTotalPrice:       'Project total',
    productSection:      'Product',
    productEmpty:        'No product linked yet for this BoQ code.',
    productUnitPrice:    'Unit price',
    productPriceUnit:    'per unit',
    productViewButton:   'View product',
    productEditButton:   'Edit',
    productAssignButton: 'Assign product',
    /* Edit-mode form labels */
    editTitleLabel:      'Product title',
    editTitlePlaceholder:'Product name…',
    editPriceLabel:      'Unit price',
    editVendorLabel:     'Vendor / source',
    editVendorPlaceholder:'Amazon, Hornbach…',
    editUrlLabel:        'Product URL',
    editImageLabel:      'Image URL',
    editSave:            'Save',
    editCancel:          'Cancel',
    editSaving:          'Saving…',
    editSaved:           'Saved.',
    editErrorPrice:      'Invalid price',
    editErrorBridge:     'Budget bridge unavailable — try again later',
    editErrorSave:       'Save failed',
    instanceSection:     'Instance',
    instanceId:          'Instance ID',
    instanceType:        'Type',
    instancePosition:    'Position',
    instanceLength:      'Length',
    instanceDimensions:  'Dimensions',
    tabsSection:         'Asset record',
    tabDocuments:        'Documents',
    tabRevisions:        'Revisions',
    tabContacts:         'Contacts',
    tabCosts:            'Costs',
    tabNotes:            'Notes',
    placeholderDocs:     'No documents linked yet. Reserved for the operate phase.',
    placeholderRevs:     'Revision history will appear here.',
    placeholderCntc:     'Suppliers, installers and warranty contacts will appear here.',
    placeholderCost:     'Cost history and offers will appear here.',
    placeholderNote:     'Notes about this instance will appear here.',
    closeLabel:          'Close drawer',
    imagePlaceholder:    'No image',
    /* Trial / non-purchased state */
    lockedOverlayTitle:  'Purchase to unlock',
    lockedOverlayBody:   'Please purchase this variant first to access the online budgeting feature and 2D blueprints.'
  };

  /* kept so the copy-log button has something to hand over */
  var _logBuf = [];
  function _log(msg) {
    try { _logBuf.push(String(msg)); if (_logBuf.length > 200) _logBuf.shift(); } catch (e) {}
    if (global.Configurator && global.Configurator.log && global.Configurator.log.log) {
      global.Configurator.log.log('[asset-drawer] ' + msg);
    }
  }

  function _esc(s) {
    if (s === null || s === undefined) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /* Resolve the display name for a BoQ instance.

     Single source of truth for the localized BoQ name: the global
     window.decoratedTitle(boqRow) — the same helper the budget drawer
     (air-custom-drawer.liquid) uses to turn a raw BoQ row into a
     localized title. When the instance carries its raw BoQ row and the
     global is present, we use it so the drawer name matches the budget
     app verbatim, including BoQ translations.

     Fallback chain (each step only taken if the previous yields an
     empty string):
       1. window.decoratedTitle(instance.boqRow)   ← localized BoQ name
       2. instance.name                            ← whatever the host set
       3. ''                                       ← caller renders '—'

     decoratedTitle may be absent when the configurator runs standalone
     (no budget app on the page); in that case we silently fall back to
     instance.name so the drawer still renders. */
  function _resolveBoqName(instance) {
    if (!instance) return '';
    if (instance.boqRow && typeof global.decoratedTitle === 'function') {
      try {
        var decorated = global.decoratedTitle(instance.boqRow);
        if (decorated !== null && decorated !== undefined) {
          decorated = String(decorated).trim();
          if (decorated) return decorated;
        }
      } catch (err) {
        _log('decoratedTitle threw: ' + (err && err.message ? err.message : err));
      }
    }
    return (instance.name === null || instance.name === undefined) ? '' : String(instance.name);
  }

  /* ---------------------------------------------------------------
     DOM TEMPLATE
     --------------------------------------------------------------- */
  function _buildHTML() {
    var L = _labels;
    var scopeClass = _drawerScopeClass ? (' ' + _esc(_drawerScopeClass)) : '';
    return [
      '<div class="side-panel configurator-asset-drawer' + scopeClass + '"',
      '     id="' + DRAWER_ID + '" tabindex="-1" role="dialog" aria-modal="true"',
      '     aria-labelledby="asset-drawer-title">',
      '  <div class="side-panel-inner">',
      '    <div class="side-panel-header">',
      /* the copy-log button is positioned against this, so it has to be the
         positioned parent -- without it the button sat outside the header and
         was invisible (Marek: copy log button nevidim) */
      '      <div style="position:relative;">',
      '        <span class="drawer-header-content">',
      '          <span class="asset-drawer-title" id="asset-drawer-title" data-field="title">' + _esc(L.titlePlaceholder) + '</span>',
      '          <span class="asset-drawer-subtitle" data-field="subtitle">—</span>',
      '        </span>',
      /* the admin's COPY LOG icon is gone (asset-drawer-v19, Marek 2026-10-31) */
      '        <side-panel-close class="side-panel-close" data-asset-drawer-close',
      '                          aria-label="' + _esc(L.closeLabel) + '">',
      '          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"',
      '               stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">',
      '            <path d="M6 6l12 12M18 6L6 18"/>',
      '          </svg>',
      '        </side-panel-close>',
      '      </div>',
      '    </div>',
      '    <div class="side-panel-content">',
      '      <div class="asset-drawer-body">',

      /* SECTION 1 — BoQ (collective). */
      '        <section class="asset-drawer-card" data-section="boq">',
      '          <h3 class="asset-drawer-card__heading">' + _esc(L.boqSection) + '</h3>',
      '          <div class="asset-drawer-grid">',
      '            <span class="asset-drawer-grid__label">' + _esc(L.boqName) + '</span>',
      '            <span class="asset-drawer-grid__value" data-field="boq-name">—</span>',
      '            <span class="asset-drawer-grid__label">' + _esc(L.boqCode) + '</span>',
      '            <span class="asset-drawer-grid__value asset-drawer-grid__value--code" data-field="boq-code">—</span>',
      '            <span class="asset-drawer-grid__label">' + _esc(L.boqCategory) + '</span>',
      '            <span class="asset-drawer-grid__value" data-field="boq-category">—</span>',
      '            <span class="asset-drawer-grid__label">' + _esc(L.boqUnit) + '</span>',
      '            <span class="asset-drawer-grid__value" data-field="boq-unit">—</span>',
      '            <span class="asset-drawer-grid__label">' + _esc(L.boqQuantity) + '</span>',
      '            <span class="asset-drawer-grid__value" data-field="boq-quantity">—</span>',
      '            <span class="asset-drawer-grid__label">' + _esc(L.boqUnitPrice) + '</span>',
      '            <span class="asset-drawer-grid__value" data-field="boq-unit-price">—</span>',
      '            <span class="asset-drawer-grid__label">' + _esc(L.boqTotalPrice) + '</span>',
      '            <span class="asset-drawer-grid__value asset-drawer-grid__value--accent" data-field="boq-total-price">—</span>',
      '          </div>',
      '        </section>',

      /* SECTION 2 — Product (Shopify product assigned to this BoQ code).
         Filled by _renderProduct() when instance.product is present;
         otherwise replaced with an empty-state message. */
      '        <section class="asset-drawer-card" data-section="product">',
      '          <h3 class="asset-drawer-card__heading">' + _esc(L.productSection) + '</h3>',
      '          <div data-field="product-body">',
      '            <p class="asset-drawer-product--empty">' + _esc(L.productEmpty) + '</p>',
      '          </div>',
      '        </section>',

      /* SECTION 3 — Instance (per-piece). */
      '        <section class="asset-drawer-card" data-section="instance">',
      '          <h3 class="asset-drawer-card__heading">' + _esc(L.instanceSection) + '</h3>',
      '          <div class="asset-drawer-grid">',
      '            <span class="asset-drawer-grid__label">' + _esc(L.instanceId) + '</span>',
      '            <span class="asset-drawer-grid__value asset-drawer-grid__value--mono" data-field="instance-id">—</span>',
      '            <span class="asset-drawer-grid__label">' + _esc(L.instanceType) + '</span>',
      '            <span class="asset-drawer-grid__value" data-field="instance-type">—</span>',
      '            <span class="asset-drawer-grid__label">' + _esc(L.instancePosition) + '</span>',
      '            <span class="asset-drawer-grid__value asset-drawer-grid__value--mono" data-field="instance-position">—</span>',
      '            <span class="asset-drawer-grid__label" data-field-label="instance-length">' + _esc(L.instanceLength) + '</span>',
      '            <span class="asset-drawer-grid__value" data-field="instance-length">—</span>',
      '          </div>',
      '        </section>',

      /* SECTION 4 — Operate-phase tabs (UI shell only). */
      '        <section class="asset-drawer-card" data-section="record">',
      '          <h3 class="asset-drawer-card__heading">' + _esc(L.tabsSection) + '</h3>',
      '          <div class="asset-drawer-tabs" role="tablist">',
      '            <button type="button" role="tab" class="asset-drawer-tab tab-active" data-asset-tab="documents" aria-selected="true">' + _esc(L.tabDocuments) + '</button>',
      '            <button type="button" role="tab" class="asset-drawer-tab"            data-asset-tab="revisions" aria-selected="false">' + _esc(L.tabRevisions) + '</button>',
      '            <button type="button" role="tab" class="asset-drawer-tab"            data-asset-tab="contacts"  aria-selected="false">' + _esc(L.tabContacts)  + '</button>',
      '            <button type="button" role="tab" class="asset-drawer-tab"            data-asset-tab="costs"     aria-selected="false">' + _esc(L.tabCosts)     + '</button>',
      '            <button type="button" role="tab" class="asset-drawer-tab"            data-asset-tab="notes"     aria-selected="false">' + _esc(L.tabNotes)     + '</button>',
      '          </div>',
      '          <div class="asset-drawer-tab-panel tab-active" data-asset-panel="documents" role="tabpanel">',
      '            <p class="asset-drawer-placeholder">' + _esc(L.placeholderDocs) + '</p>',
      '          </div>',
      '          <div class="asset-drawer-tab-panel" data-asset-panel="revisions" role="tabpanel">',
      '            <p class="asset-drawer-placeholder">' + _esc(L.placeholderRevs) + '</p>',
      '          </div>',
      '          <div class="asset-drawer-tab-panel" data-asset-panel="contacts" role="tabpanel">',
      '            <p class="asset-drawer-placeholder">' + _esc(L.placeholderCntc) + '</p>',
      '          </div>',
      '          <div class="asset-drawer-tab-panel" data-asset-panel="costs" role="tabpanel">',
      '            <p class="asset-drawer-placeholder">' + _esc(L.placeholderCost) + '</p>',
      '          </div>',
      '          <div class="asset-drawer-tab-panel" data-asset-panel="notes" role="tabpanel">',
      '            <p class="asset-drawer-placeholder">' + _esc(L.placeholderNote) + '</p>',
      '          </div>',
      '        </section>',

      '      </div>',
      /* Locked overlay — covers the entire body when the user hasn't
         purchased the project variant yet. Body content stays in the
         DOM (so screen readers / SEO still see it) but is visually
         dimmed and non-interactive via CSS. */
      '      <div class="asset-drawer-lock" data-asset-drawer-lock aria-hidden="true">',
      '        <div class="asset-drawer-lock__panel">',
      '          <svg class="asset-drawer-lock__icon" viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">',
      '            <rect x="3" y="11" width="18" height="11" rx="2" />',
      '            <path d="M7 11V7a5 5 0 0 1 10 0v4" />',
      '          </svg>',
      '          <h4 class="asset-drawer-lock__title" data-field="locked-title">' + _esc(L.lockedOverlayTitle) + '</h4>',
      '          <p class="asset-drawer-lock__body" data-field="locked-body">' + _esc(L.lockedOverlayBody) + '</p>',
      '        </div>',
      '      </div>',
      '    </div>',
      '  </div>',
      '</div>'
    ].join('\n');
  }

  /* ---------------------------------------------------------------
     MOUNT — idempotent, appends drawer to <body>
     --------------------------------------------------------------- */
  function _mount() {
    if (document.getElementById(DRAWER_ID)) return;
    var holder = document.createElement('div');
    holder.innerHTML = _buildHTML();
    var drawer = holder.firstElementChild;
    document.body.appendChild(drawer);
    _bindTabs(drawer);
    _bindCloseFallback(drawer);
    _watchActiveClass(drawer);
    _log('mounted drawer (version=' + VERSION + ')');
  }

  function _bindTabs(drawer) {
    drawer.querySelectorAll('.asset-drawer-tab').forEach(function (tab) {
      tab.addEventListener('click', function () {
        var tabId = tab.getAttribute('data-asset-tab');
        drawer.querySelectorAll('.asset-drawer-tab').forEach(function (t) {
          var active = t.getAttribute('data-asset-tab') === tabId;
          t.classList.toggle('tab-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        drawer.querySelectorAll('.asset-drawer-tab-panel').forEach(function (p) {
          p.classList.toggle('tab-active', p.getAttribute('data-asset-panel') === tabId);
        });
      });
    });
  }

  /* In Dawn theme, <side-panel-close> handles its own click → drawer
     .active toggle. We add a fallback click listener for non-Dawn
     themes; MutationObserver below catches the class change either
     way and fires onClose. ESC closes when drawer is open. */
  function _bindCloseFallback(drawer) {
    drawer.querySelectorAll('[data-asset-drawer-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (drawer.classList.contains('active')) {
          drawer.classList.remove('active');
          document.body.classList.remove('open-cc');
        }
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && _currentInstanceId !== null) {
        close();
      }
    });
  }

  function _watchActiveClass(drawer) {
    if (typeof MutationObserver !== 'function') return;
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].attributeName !== 'class') continue;
        var isActive = drawer.classList.contains('active');
        if (_currentInstanceId !== null && !isActive) {
          if (_suppressNextObserverClose) {
            _suppressNextObserverClose = false;
            return;
          }
          var prevId = _currentInstanceId;
          _currentInstanceId = null;
          if (typeof _onClose === 'function') {
            try { _onClose(prevId); } catch (err) {
              _log('onClose threw: ' + (err && err.message ? err.message : err));
            }
          }
        }
      }
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---------------------------------------------------------------
     RENDER — paint a single instance into the drawer
     --------------------------------------------------------------- */
  function _render(instance) {
    var drawer = document.getElementById(DRAWER_ID);
    if (!drawer || !instance) return;

    function set(field, value) {
      var el = drawer.querySelector('[data-field="' + field + '"]');
      if (el) el.textContent = (value === null || value === undefined || value === '') ? '—' : value;
    }

    /* Name comes from the localized BoQ row when available (so the
       drawer matches the budget app and shows BoQ translations);
       otherwise it falls back to instance.name. Resolved once and
       reused for both the header title and the BoQ Name cell. */
    var displayName = _resolveBoqName(instance);

    set('title',    displayName || _labels.titlePlaceholder);
    set('subtitle', _fmtSubtitle(instance));

    /* BoQ (collective). */
    set('boq-name',        displayName);
    set('boq-code',        instance.boqCode);
    set('boq-category',    instance.category);
    set('boq-unit',        instance.unit);
    set('boq-quantity',    _fmtQty(instance.projectQuantity, instance.unit));
    set('boq-unit-price',  _fmtPrice(instance.projectUnitPrice, instance.currency));
    set('boq-total-price', _fmtPrice(instance.projectTotalPrice, instance.currency));

    /* Instance (per-piece). */
    set('instance-id',       instance.id);
    set('instance-type',     instance.type);
    set('instance-position', _fmtPos(instance.position));

    /* Length cell behaves polymorphically:
       • For plate-like pieces (roof, decking) the instance carries a
         `dimensions: { length, width }` pair. We display "L × W m"
         and rename the label from "Length" → "Dimensions" so the
         user understands they're looking at two extents, not one.
       • For linear pieces (posts, rafters, rails) we keep the
         classic "Length: 1.70 m" rendering via _fmtLen.
       • For pieces whose unit is m² or pcs and no dimensions are
         set, _fmtLen returns null and the cell shows "—". */
    var lenLabelEl = drawer.querySelector('[data-field-label="instance-length"]');
    if (instance.dimensions
        && typeof instance.dimensions.length === 'number'
        && typeof instance.dimensions.width  === 'number') {
      set('instance-length', _fmtDims(instance.dimensions, instance.unit));
      if (lenLabelEl) lenLabelEl.textContent = _labels.instanceDimensions || 'Dimensions';
    } else {
      set('instance-length', _fmtLen(instance.length, instance.unit));
      if (lenLabelEl) lenLabelEl.textContent = _labels.instanceLength || 'Length';
    }

    /* Product (assigned Shopify product). boqCode flows down so the
       edit form knows which catalogue ID to update via the bridge. */
    _renderProduct(drawer, instance.product, instance.boqCode);

    /* Trial / non-purchased gating. When the active project variant
       hasn't been purchased, fade the drawer body and show an overlay.
       Save attempts are no-ops (handled in _submitProductEdit). The
       check goes through the bridge so it stays in sync with budget's
       window.__budgetPurchasedVariantIds list. */
    _applyLockState(drawer);
  }

  function _applyLockState(drawer) {
    var bridge = global.ConfiguratorBudgetBridge;
    var locked = false;
    if (bridge && typeof bridge.isCurrentVariantPurchased === 'function') {
      try { locked = !bridge.isCurrentVariantPurchased(); }
      catch (err) {
        _log('purchase check threw: ' + (err && err.message ? err.message : err));
        locked = false;   /* fail-open: don't punish the user for an error */
      }
    }
    drawer.classList.toggle('is-locked', locked);
    var lockEl = drawer.querySelector('[data-asset-drawer-lock]');
    if (lockEl) lockEl.setAttribute('aria-hidden', locked ? 'false' : 'true');
  }

  /* Product section has two modes: VIEW (default) and EDIT.
     VIEW shows assigned product (image, title, price, "View product"
     button). EDIT shows form inputs (title, price, vendor, URL, image
     URL) with Save / Cancel buttons.

     When no product is assigned, VIEW shows empty state + "Assign"
     button which switches to EDIT mode with blank fields.

     EDIT mode submits through ConfiguratorBudgetBridge which routes
     to the budget app's saveAffiliateProductBasics → Firestore. */
  var _editMode = false;
  var _currentBoqCode = null;

  function _renderProduct(drawer, product, boqCode) {
    var body = drawer.querySelector('[data-field="product-body"]');
    if (!body) return;
    _currentBoqCode = boqCode || null;

    /* NO EDITING HERE. The card used to open its own fields for typing a
       vendor, a URL and a price -- a second interface for something the budget
       already does properly, on its item detail, with the search, the offers
       and the price history. Two interfaces for one job drift apart, and the
       one that drifts is always the smaller one (Marek: uplne zbytecne,
       zrusit; vsechno na karte detailu u budgetu). The card shows and the
       button opens THAT drawer. */
    _renderProductViewMode(body, product);
  }

  function _renderProductViewMode(body, product) {
    var L = _labels;
    var hasProduct = product && (product.title || product.image || product.price);

    if (!hasProduct) {
      body.innerHTML =
        '<p class="asset-drawer-product--empty">' + _esc(L.productEmpty) + '</p>' +
        '<div class="asset-drawer-product__actions">' +
        '  <button type="button" class="asset-drawer-product__cta"' +
        '          data-asset-item-detail aria-label="' + _esc(L.productAssignButton || 'Assign product') + '">' +
                _esc(L.productAssignButton || 'Assign product') +
        '  </button>' +
        '</div>';
      _bindProductActions(body);
      return;
    }

    var imageHTML = product.image
      ? '<img class="asset-drawer-product__image" src="' + _esc(product.image) + '" alt="' + _esc(product.title || '') + '" loading="lazy">'
      : '<span class="asset-drawer-product__image-placeholder">' + _esc(L.imagePlaceholder) + '</span>';

    /* THE BUDGET'S OWN SHAPE, NOT AN APPROXIMATION OF IT.

       On the item drawer the vendor is a grey chip, the amount is large and
       heavy, and the words "Unit price" sit UNDER it in small grey -- in that
       order. The card had its own arrangement and its own weights, so the two
       read as different things while showing the same number (Marek: stejny
       font Unit price a castka, stejne prvky). These are the budget's classes
       with the budget's inline weights, so they match whatever the theme does
       to them. */
    var priceHTML = '';
    if (product.price != null) {
      priceHTML =
        '<div class="asset-drawer-product__price" style="font:800 28px/1.1 Montserrat,sans-serif;color:#1A1C1D;margin-top:10px;">' +
        (_fmtUnitPriceHtml(product.price, product.currency) || '') + '</div>' +
        '<div class="budget-item__col-label" style="font:600 12px/1.3 Montserrat,sans-serif;color:#9CA3AF;margin-top:2px;">' +
        _esc(L.productUnitPrice || 'Unit price') + (product.unit ? (' / ' + _esc(product.unit)) : '') + '</div>';
    }

    /* THE VENDOR AS THE ITEM DRAWER WRITES IT: as it came from the shop, not
       shouted in capitals, and the grey sits behind the NAME only rather than
       running the width of the row (Marek). */
    var vendorHTML = product.source
      ? '<div style="margin-top:8px;"><span style="display:inline-block;font:500 12px/1 ui-monospace,Menlo,monospace;' +
        'color:#4B5563;background:#F3F4F6;border-radius:6px;padding:5px 9px;text-transform:none;">' +
        _esc(product.source) + '</span></div>'
      : '';

    var viewHref = product.url || (product.handle ? ('/products/' + product.handle) : '');
    var viewBtn = viewHref
      ? '<a class="asset-drawer-product__cta" href="' + _esc(viewHref) + '" target="_blank" rel="noopener">' +
                  _esc(L.productViewButton) +
                  ' <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
                  '<path d="M7 17L17 7M9 7h8v8"/></svg></a>'
      : '';

    /* A SQUARE ICON BUTTON with rounded corners, the same one the item drawer
       uses -- not a word (Marek: ma tam byt ctvercova ikona round corner). */
    var editBtn =
      '<button type="button" data-asset-item-detail aria-label="' + _esc(L.productEditButton || 'Edit') + '"' +
      '        title="' + _esc(L.productEditButton || 'Edit') + '"' +
      '        style="width:44px;height:44px;border-radius:12px;border:1px solid #E5E7EB;background:#F3F4F6;' +
      'display:inline-flex;align-items:center;justify-content:center;cursor:pointer;color:#1A1C1D;flex:0 0 auto;">' +
      '  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.9"' +
      '       stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/>' +
      '    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>' +
      '</button>';

    body.innerHTML = [
      /* A WIDER PICTURE. 96 px made every board look the same; the item drawer
         gives the photo real room and it is the one thing on the card that
         says what the product IS (Marek: obrazek vetsi, sirsi sloupec). */
      '<div class="asset-drawer-product" style="grid-template-columns:150px minmax(0,1fr);gap:18px;">',
      '  <div class="asset-drawer-product__image-wrap" style="width:150px;height:150px;border-radius:14px;">' + imageHTML + '</div>',
      '  <div class="asset-drawer-product__content">',
      '    <h4 class="asset-drawer-product__title">' + _esc(product.title || '') + '</h4>',
            vendorHTML,
      /* THE BUTTON SITS BESIDE THE PRICE, not on a row of its own below it --
         that is where the item drawer keeps it and there is nothing else
         competing for the space (Marek). One button: the shop link is on the
         item's own card in the budget, so here it would be a second way to
         the same place. */
      '    <div style="display:flex;align-items:flex-end;justify-content:space-between;gap:12px;">',
      '      <div>' + priceHTML + '</div>',
      '      <div class="asset-drawer-product__actions" style="margin:0;">' + editBtn + '</div>',
      '    </div>',
      '  </div>',
      '</div>'
    ].join('');
    _bindProductActions(body);
  }

  function _renderProductEditMode(body, product) {
    var L = _labels;
    var titleVal  = product.title  || '';
    var urlVal    = product.url    || '';
    var imageVal  = product.image  || '';
    var vendorVal = product.source || '';
    var priceVal  = (product.price != null) ? product.price : '';
    var currency  = product.currency || '';

    body.innerHTML = [
      '<form class="asset-drawer-product-edit" data-asset-product-form>',
      '  <div class="asset-drawer-edit-row">',
      '    <label class="asset-drawer-edit-label" for="asset-edit-title">' + _esc(L.editTitleLabel || 'Product title') + '</label>',
      '    <input type="text" id="asset-edit-title" class="asset-drawer-edit-input" data-field-edit="title"',
      '           value="' + _esc(titleVal) + '" placeholder="' + _esc(L.editTitlePlaceholder || 'e.g. 8×100mm wood screws, 100 pcs') + '">',
      '  </div>',
      '  <div class="asset-drawer-edit-row asset-drawer-edit-row--split">',
      '    <div>',
      '      <label class="asset-drawer-edit-label" for="asset-edit-price">' + _esc(L.editPriceLabel || 'Unit price') + (currency ? ' (' + _esc(currency) + ')' : '') + '</label>',
      '      <input type="number" id="asset-edit-price" class="asset-drawer-edit-input" data-field-edit="price"',
      '             value="' + _esc(String(priceVal)) + '" min="0" step="0.01" placeholder="0.00">',
      '    </div>',
      '    <div>',
      '      <label class="asset-drawer-edit-label" for="asset-edit-vendor">' + _esc(L.editVendorLabel || 'Vendor / source') + '</label>',
      '      <input type="text" id="asset-edit-vendor" class="asset-drawer-edit-input" data-field-edit="source"',
      '             value="' + _esc(vendorVal) + '" placeholder="' + _esc(L.editVendorPlaceholder || 'Amazon, Hornbach…') + '">',
      '    </div>',
      '  </div>',
      '  <div class="asset-drawer-edit-row">',
      '    <label class="asset-drawer-edit-label" for="asset-edit-url">' + _esc(L.editUrlLabel || 'Product URL') + '</label>',
      '    <input type="url" id="asset-edit-url" class="asset-drawer-edit-input" data-field-edit="url"',
      '           value="' + _esc(urlVal) + '" placeholder="https://…">',
      '  </div>',
      '  <div class="asset-drawer-edit-row">',
      '    <label class="asset-drawer-edit-label" for="asset-edit-image">' + _esc(L.editImageLabel || 'Image URL') + '</label>',
      '    <input type="url" id="asset-edit-image" class="asset-drawer-edit-input" data-field-edit="image"',
      '           value="' + _esc(imageVal) + '" placeholder="https://…">',
      '  </div>',
      '  <p class="asset-drawer-edit-status" data-asset-edit-status></p>',
      '  <div class="asset-drawer-edit-actions">',
      '    <button type="button" class="asset-drawer-product__cta asset-drawer-product__cta--ghost" data-asset-product-cancel>',
            _esc(L.editCancel || 'Cancel'),
      '    </button>',
      '    <button type="submit" class="asset-drawer-product__cta" data-asset-product-save>',
            _esc(L.editSave || 'Save'),
      '    </button>',
      '  </div>',
      '</form>'
    ].join('');
    _bindProductEditForm(body);
  }

  /* THE BUTTON GOES TO THE BUDGET'S ITEM DETAIL -- the card with the vendor
     search, the offers, the price and the history. window.levaOpenItemDetail
     is published by budget-core for exactly this, so both places open the same
     drawer and there is no second editor to keep in step (Marek). */
  function _bindProductActions(body) {
    var btn = body.querySelector('[data-asset-item-detail]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var drawer = document.getElementById(DRAWER_ID);
      if (drawer && drawer.classList.contains('is-locked')) return;
      /* EVERY STEP SAID OUT LOUD.

         The button has been dead for several rounds and each guess at why cost
         a round, because nothing on this path reports anything. Now it does:
         what it was asked to open, whether the opener exists, what it returned,
         and whether the drawer element appeared afterwards. One click, one
         answer (Marek). */
      var code = _currentBoqCode || _currentInstanceId;
      _log('item detail: click, code=' + String(code) +
           ' opener=' + (typeof global.levaOpenItemDetail) +
           ' wiring=' + ((global.LevaBudgetWiring && global.LevaBudgetWiring.VERSION) || 'none'));
      if (!code) { _log('item detail: no code on the card -- nothing to open'); return; }
      var ret;
      try {
        if (typeof global.levaOpenItemDetail === 'function') {
          ret = global.levaOpenItemDetail(code, 'material');
          _log('item detail: opener returned ' + JSON.stringify(ret));
          global.setTimeout(function () {
            var d = document.getElementById('Custom-Drawer');
            _log('item detail: 300 ms later, Custom-Drawer ' +
                 (d ? ('is ' + (d.classList.contains('active') ? 'OPEN' : 'in the page but CLOSED')) : 'NOT IN THE PAGE'));
          }, 300);
          return;
        }
      } catch (e) { _log('item detail: opener threw ' + ((e && e.message) || e)); return; }
      _log('item detail: levaOpenItemDetail missing -- is the budget on the page?');
    });
  }

  function _bindProductEditForm(body) {
    var form = body.querySelector('[data-asset-product-form]');
    if (!form) return;

    var cancelBtn = body.querySelector('[data-asset-product-cancel]');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', function (e) {
        e.preventDefault();
        _editMode = false;
        var drawer = document.getElementById(DRAWER_ID);
        if (!drawer || !_currentInstanceId || !_registry) return;
        var instance = _registry(_currentInstanceId);
        if (instance) _renderProduct(drawer, instance.product, _currentBoqCode);
      });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      _submitProductEdit(body);
    });
  }

  function _submitProductEdit(body) {
    if (!_currentBoqCode) {
      _log('submit blocked: no current boqCode');
      return;
    }
    /* Locked (trial) state — Save button is a no-op per UX decision.
       The overlay already explains why; we don't add a warning modal. */
    var drawer = document.getElementById(DRAWER_ID);
    if (drawer && drawer.classList.contains('is-locked')) {
      _log('submit blocked: drawer is in locked (non-purchased) state');
      return;
    }
    var fields = {};
    body.querySelectorAll('[data-field-edit]').forEach(function (input) {
      fields[input.getAttribute('data-field-edit')] = input.value.trim();
    });
    if (fields.price !== '' && fields.price != null) {
      fields.price = parseFloat(fields.price);
      if (!isFinite(fields.price) || fields.price < 0) {
        _setEditStatus(body, _labels.editErrorPrice || 'Invalid price', 'error');
        return;
      }
    }

    var saveBtn = body.querySelector('[data-asset-product-save]');
    if (saveBtn) {
      saveBtn.setAttribute('disabled', 'disabled');
      saveBtn.textContent = _labels.editSaving || 'Saving…';
    }
    _setEditStatus(body, _labels.editSaving || 'Saving…', 'info');

    var bridge = global.ConfiguratorBudgetBridge;
    if (!bridge || typeof bridge.saveAffiliateProduct !== 'function') {
      _setEditStatus(body, _labels.editErrorBridge || 'Budget bridge unavailable', 'error');
      if (saveBtn) {
        saveBtn.removeAttribute('disabled');
        saveBtn.textContent = _labels.editSave || 'Save';
      }
      return;
    }

    bridge.saveAffiliateProduct(_currentBoqCode, fields)
      .then(function () {
        _setEditStatus(body, _labels.editSaved || 'Saved.', 'success');
        /* Switch back to view mode after a short delay so user sees
           confirmation. The instance will be re-fetched via registry
           so the product appears with the new values. */
        setTimeout(function () {
          _editMode = false;
          var drawer = document.getElementById(DRAWER_ID);
          if (!drawer || !_currentInstanceId || !_registry) return;
          var instance = _registry(_currentInstanceId);
          if (instance) _render(instance);
        }, 600);
      })
      .catch(function (err) {
        _setEditStatus(body, (_labels.editErrorSave || 'Save failed') + ': ' + (err && err.message ? err.message : err), 'error');
        if (saveBtn) {
          saveBtn.removeAttribute('disabled');
          saveBtn.textContent = _labels.editSave || 'Save';
        }
      });
  }

  function _setEditStatus(body, text, kind) {
    var el = body.querySelector('[data-asset-edit-status]');
    if (!el) return;
    el.textContent = text || '';
    el.className = 'asset-drawer-edit-status' + (kind ? ' asset-drawer-edit-status--' + kind : '');
  }

  function _fmtSubtitle(i) {
    var parts = [];
    if (i.boqCode) parts.push(i.boqCode);
    if (i.type && i.type !== i.boqCode && i.type !== i.name) parts.push(i.type);
    return parts.join(' · ');
  }
  /* Format a project quantity for the drawer. Three signals govern
     what unit suffix to show:
       1. The BoQ unit string passed in (preferred) — e.g. "M2", "FT".
          The central sheet supplies localized variants; we surface
          them verbatim so a CS-locale drawer reads "ks" and an
          EN-locale one reads "pcs" without further translation here.
       2. If the unit is missing (sheet not loaded or row incomplete),
          we fall back to a sensible neutral string — "—" rather than
          a misleading guess. The instance ID + drawer title still
          identify what the user is looking at.
     We always round to 2 decimals; raw float values like 2.8943
     m² are noisy and don't help the user pick a panel format. */
  function _fmtQty(q, unit) {
    if (typeof q !== 'number' || !isFinite(q)) return null;
    var rounded = Math.round(q * 100) / 100;
    var u = (unit && String(unit).trim()) || '';
    return u ? (rounded + ' ' + u) : String(rounded);
  }
  /* Price formatting. We prefer window.formatPrice (defined in
     budget-prd-cat-selector.liquid) so the drawer matches the budget
     app exactly — same currency symbol position, same thousands
     separator, same fallback chain across 25+ currencies. Only when
     budget-prd-cat-selector isn't on the page do we fall back to a
     local mini-formatter so the configurator still renders standalone. */
  /* A UNIT PRICE KEEPS ITS CENTS.

     0.87 was shown as "$1" -- rounded to whole money, which is right for a
     line total and wrong for a price per unit, where the cents ARE the number.
     The item drawer uses Intl with two decimals fixed (formatUnitMoney); this
     is the same rule, so the two cards read the same (Marek: musi ukazovat i
     setiny, udelat to stejne jako item drawer). formatPrice from the page is
     the whole-money formatter, so it is deliberately not used here. */
  /* CENTS RIDE HIGH, WITH NO SEPARATOR -- the item drawer's own shape:
     $0 with a small raised 87, not "$0.87" and not "$1" (Marek, twice). The
     markup and the class are copied from formatUnitPriceHtml in drawer.js so
     the two cards cannot drift apart. */
  function _curSymbol(currency) {
    var a = (global.Shopify && global.Shopify.currency && global.Shopify.currency.active) || currency || 'USD';
    return ({ USD: '$', EUR: '\u20AC', GBP: '\u00A3', CZK: 'K\u010D', PLN: 'z\u0142' })[a] || (a + ' ');
  }
  function _fmtUnitPriceHtml(p, currency) {
    if (typeof p !== 'number' || !isFinite(p)) return null;
    var whole = Math.floor(Math.abs(p));
    var cents = Math.round((Math.abs(p) - whole) * 100);
    if (cents === 100) { whole += 1; cents = 0; }
    var c = (cents < 10 ? '0' : '') + cents;
    var sym = _curSymbol(currency);
    return _esc(sym) + (p < 0 ? '-' : '') + whole +
           '<sup class="leva-frac" style="font-size:.5em;vertical-align:text-top;line-height:1;' +
           'margin-left:1px;font-weight:inherit;position:relative;top:.12em;">' + c + '</sup>';
  }

  function _fmtPrice(p, currency) {
    if (typeof p !== 'number' || !isFinite(p)) return null;
    if (typeof global.formatPrice === 'function') {
      try { return global.formatPrice(p); }
      catch (e) { /* fall through to local impl */ }
    }
    /* Local fallback — kept minimal because in practice budget is
       loaded alongside the configurator on every product page. */
    var active = (global.Shopify && global.Shopify.currency && global.Shopify.currency.active)
                 || currency || 'USD';
    var pretty = (Math.round(p * 100) / 100).toLocaleString('en-US', {
      minimumFractionDigits: 0, maximumFractionDigits: 0
    }).replace(/,/g, ' ');
    switch (active) {
      case 'USD': return '$' + pretty;
      case 'EUR': return '€' + pretty;
      case 'GBP': return '£' + pretty;
      case 'CZK': return pretty + ' Kč';
      case 'PLN': return pretty + ' zł';
      case 'CHF': return 'CHF ' + pretty;
      default:    return active + ' ' + pretty;
    }
  }
  function _fmtPos(p) {
    if (!p || typeof p.x !== 'number') return null;
    return 'x ' + p.x.toFixed(2) + '  y ' + p.y.toFixed(2) + '  z ' + p.z.toFixed(2);
  }
  function _fmtLen(l, unit) {
    if (typeof l !== 'number' || !isFinite(l)) return null;
    /* Length cell is meaningful for linear-meter items (m). For m² or pcs
       the project quantity already conveys the size — hide the row by
       returning null (set() then renders '—'). */
    if (unit === 'm²' || unit === 'pcs') return null;
    return l.toFixed(3) + ' ' + (unit || 'm');
  }

  /* Given the BoQ item's unit (which can be linear m/ft, area m²/ft², or
     count pcs), return the LINEAR unit appropriate for displaying a
     dimension. Linear units pass through ('m' → 'm', 'ft' → 'ft'); area
     units map to their linear root ('m²' → 'm', 'ft²' → 'ft'); count
     units fall back to 'm' (sensible default — dimensions are metric
     internally; the imperial flag drives the conversion before this
     formatter is called).

     We accept several spellings the central sheet may use: "M2", "m²",
     "M²", "sqm" all collapse to area. Same for cubic. */
  function _linearUnitFor(unit) {
    if (!unit) return 'm';
    var u = String(unit).trim().toLowerCase();
    if (u === 'ft' || u === 'feet' || u === 'foot') return 'ft';
    if (u === 'm' || u === 'meter' || u === 'meters')    return 'm';
    if (u === 'ft²' || u === 'ft2' || u === 'sqft')       return 'ft';
    if (u === 'm²'  || u === 'm2'  || u === 'sqm')        return 'm';
    if (u === 'ft³' || u === 'ft3')                       return 'ft';
    if (u === 'm³'  || u === 'm3')                        return 'm';
    return 'm';   /* default: metric linear */
  }

  /* Format a 2D extent for plate-like pieces (roof, decking). Returns
     "L × W m" (or "ft" in imperial) rounded to 2 decimals — the same
     precision the PDF cut-list page uses, so the numbers stay
     consistent across surfaces.

     The dimensions stored in instance.dimensions are ALWAYS metric (the
     renderer doesn't convert geometry). We pull the display unit from
     the BoQ item's unit string (m² → m, ft² → ft) and convert the
     numbers when needed. */
  function _fmtDims(dims, unit) {
    if (!dims) return null;
    var l = dims.length, w = dims.width;
    if (typeof l !== 'number' || !isFinite(l)) return null;
    if (typeof w !== 'number' || !isFinite(w)) return null;
    var linearUnit = _linearUnitFor(unit);
    /* Convert metric → imperial if the active display unit is feet. */
    if (linearUnit === 'ft') {
      l = l * 3.281;
      w = w * 3.281;
    }
    return l.toFixed(2) + ' × ' + w.toFixed(2) + ' ' + linearUnit;
  }

  /* ---------------------------------------------------------------
     PUBLIC API
     --------------------------------------------------------------- */
  function init(opts) {
    opts = opts || {};
    _registry         = opts.registry || null;
    _onClose          = opts.onClose  || null;
    _drawerScopeClass = opts.drawerScopeClass || '';
    if (opts.labels && typeof opts.labels === 'object') {
      for (var k in opts.labels) {
        if (opts.labels.hasOwnProperty(k) && typeof opts.labels[k] === 'string') {
          _labels[k] = opts.labels[k];
        }
      }
    }
    _mount();
    _subscribeToBridge();
  }

  /* When the budget bridge writes a new affiliate product, re-render
     the currently-open drawer so the user sees the saved values
     reflected in the BoQ totals and Product section. */
  function _subscribeToBridge() {
    if (!global.addEventListener) return;
    global.addEventListener('configurator:user-prices-updated', function (e) {
      if (!_currentInstanceId || !_registry) return;
      var updated = e && e.detail && e.detail.boqCode;
      var instance = _registry(_currentInstanceId);
      if (!instance) return;
      /* Two cases:
           updated === null/undefined → global change (variant flip,
             user_prices wholesale refresh). Re-render unconditionally
             so the lock state and prices update.
           updated === string → single-BoQ save; only re-render if
             our open instance is affected. */
      if (updated && updated !== instance.boqCode) return;
      _render(instance);
    });
  }

  /* Open the drawer for a given instance.
     If already open with a different instance, AUTO-SWITCH the content
     in-place (no flicker, no close+reopen) — Marek's explicit UX choice
     for "click another element while drawer is open". */
  /* the admin flag: whatever the page already knows. The theme sets one of
     these on a staff account; none of them means a buyer, and the button
     stays hidden. */
  function _isAdmin() {
    try {
      if (global.LEVA_IS_ADMIN === true) return true;
      /* what this theme actually publishes: customer.tags contains 'admin' */
      if (global.__levaExtrasAdmin === true) return true;
      if (global.SAUNA_BOOT && global.SAUNA_BOOT.admin) return true;
      if (global.HOUSE_BOOT && global.HOUSE_BOOT.admin) return true;
      if (/[?&](admin|stripdbg)=1/.test(global.location.search || '')) return true;
    } catch (e) {}
    return false;
  }
  var _lastInstance = null;
  function _copyLogText() {
    var out = [];
    out.push('=== ASSET DRAWER ' + VERSION + ' ===');
    out.push('instance id: ' + String(_currentInstanceId));
    try { out.push('instance: ' + JSON.stringify(_lastInstance, null, 1)); }
    catch (e) { out.push('instance: (not serialisable) ' + ((e && e.message) || e)); }
    try {
      var r = _lastInstance && _lastInstance.boqRow;
      out.push('boqRow: ' + (r ? JSON.stringify(r).slice(0, 1200) : 'NONE -- the code is not in window.productData'));
    } catch (e2) {}
    try {
      out.push('productData rows: ' + ((global.productData && global.productData.length) || 0));
      out.push('decoratedTitle: ' + (typeof global.decoratedTitle));
      out.push('budget bridge: ' + (global.ConfiguratorBudgetBridge ? 'present' : 'MISSING'));
    } catch (e3) {}
    /* WHAT THE CARD DEPENDS ON, NAMED. Two rounds went on guessing why the
       price was blank and the button dead; both hang on things that either
       exist on the page or do not, and that is one line to print (Marek). */
    try {
      out.push('wiring:');
      out.push('  LevaBudgetCore     ' + (global.LevaBudgetCore ? 'present' : 'MISSING'));
      out.push('  levaOpenItemDetail ' + (typeof global.levaOpenItemDetail));
      out.push('  LevaBudgetWiring   ' + ((global.LevaBudgetWiring && global.LevaBudgetWiring.VERSION) || 'MISSING'));
      out.push('  wiring probe       ' + (global.__levaWiringProbe || '(none -- old asset)'));
      out.push('  unitSelector       ' + (function () {
        try { var e = document.getElementById('unitSelector'); return e ? (e.getAttribute('data-current-unit') || '(no attr)') : 'NOT IN THE PAGE'; }
        catch (e) { return '?'; }
      })());
      out.push('  _currentBoqCode    ' + String(_currentBoqCode));
      out.push('  button in the DOM  ' + (document.querySelector('[data-asset-item-detail]') ? 'yes' : 'NO'));
      var c0 = _currentBoqCode || _currentInstanceId;
      if (global.LevaBudgetCore && global.LevaBudgetCore.costOf && c0) {
        out.push('  costOf(' + c0 + ', 1) -> ' + JSON.stringify(global.LevaBudgetCore.costOf(c0, 1)));
      }
      try {
        var up0 = global.user_prices || {};
        out.push('  user_prices keys   ' + Object.keys(up0).length +
                 ' | ' + c0 + ' = ' + JSON.stringify(up0[c0]) +
                 ' | source = ' + JSON.stringify(up0[c0 + '_affiliate_product_source']));
      } catch (e) {}
    } catch (e5) {}
    try { out.push('log:'); out.push((_logBuf || []).join('\n')); } catch (e4) {}
    out.push('=== END ===');
    return out.join('\n');
  }
  function _wireCopyLog() {
    var btn = document.querySelector('[data-asset-drawer-copylog]');
    if (!btn || btn.__wired) return;
    btn.__wired = true;
    /* SHOWN BY DEFAULT. The admin signal this page carries is a liquid
       variable (cfg_is_admin) that never reaches JavaScript, so every test
       here came back false and the button was never drawn -- which is why it
       was nowhere (Marek: copy log nikde). Until the flag is published to the
       page, the button is simply there; it copies a diagnostic and nothing
       else, so a buyer seeing it costs nothing. Set window.LEVA_IS_ADMIN =
       false to hide it. */
    btn.hidden = !_isAdmin() && (global.__levaExtrasAdmin !== undefined);
    btn.addEventListener('click', function (ev) {
      ev.preventDefault(); ev.stopPropagation();
      var txt = _copyLogText();
      function done() { btn.style.opacity = '1'; setTimeout(function () { btn.style.opacity = '.65'; }, 900); }
      try {
        if (global.navigator && global.navigator.clipboard && global.navigator.clipboard.writeText) {
          global.navigator.clipboard.writeText(txt).then(done, function () { global.prompt('LOG', txt); });
          return;
        }
      } catch (e) {}
      global.prompt('LOG', txt);
    });
  }

  function open(instanceId) {
    if (!_registry) {
      _log('open() before init() — registry missing');
      return;
    }
    var instance;
    try { instance = _registry(instanceId); }
    catch (err) { _log('registry threw: ' + (err && err.message ? err.message : err)); return; }
    if (!instance) {
      _log('instance not found in registry: ' + instanceId);
      return;
    }
    _mount();
    _wireCopyLog();
    _lastInstance = instance;
    /* Always open in VIEW mode; auto-switch between instances should
       not preserve an in-progress edit on a different item. */
    _editMode = false;
    _render(instance);
    var drawer = document.getElementById(DRAWER_ID);
    if (!drawer) return;
    var wasOpen = _currentInstanceId !== null;
    _currentInstanceId = instanceId;
    if (!wasOpen) {
      drawer.classList.add('active');
      document.body.classList.add('open-cc');
    }
  }

  function close() {
    var drawer = document.getElementById(DRAWER_ID);
    var prevId = _currentInstanceId;
    _currentInstanceId = null;
    _editMode = false;            /* drop any in-progress edit */
    _currentBoqCode = null;
    if (drawer && drawer.classList.contains('active')) {
      _suppressNextObserverClose = true;
      drawer.classList.remove('active');
      document.body.classList.remove('open-cc');
    }
    if (prevId !== null && typeof _onClose === 'function') {
      try { _onClose(prevId); }
      catch (err) { _log('onClose threw: ' + (err && err.message ? err.message : err)); }
    }
  }

  function isOpen() {
    return _currentInstanceId !== null;
  }

  function getSelected() {
    return _currentInstanceId;
  }

  global.ConfiguratorAssetDrawer = {
    __version:   VERSION,
    init:        init,
    open:        open,
    close:       close,
    isOpen:      isOpen,
    getSelected: getSelected
  };

})(window);